import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { InlineAlert } from '@saucelabs/ds-alert';
import { Link } from '@saucelabs/ds-link';
import { records as appStorageRecords } from '@saucelabs/sl-module-app-storage';
import { ACCOUNT_TYPES, USER_TYPE_FREE, USER_TYPE_FREEMIUM } from '@saucelabs/sl-module-user/lib/constants';
import { Units } from '@saucelabs/sl-module-usage/lib/constants';

import { GlobalState } from '../../../reducers';
import {
  trackEvent,
  EVENTS,
  LIVE_TESTING_TRACKING_PROPS,
  LIVE_TESTING_PRODUCT_SUB_AREAS,
  MANUAL_EVENTS,
} from '../../../tracking';
import { TEST_CONFIGURATION_TABS, CONFIGURATION_TEST_TYPES } from '../../../constants';
import { utils as mobileAppSelectionUtils } from '../../mobileAppSelection';
import { selectors as testConfigSelectors } from '../../testConfiguration';
import { selectors } from '../';
import { getDaysUntilRefresh } from '../../../utils';

const trackClick = (testType) => {
  trackEvent(MANUAL_EVENTS.NAVIGATE_TO_MOBILE_VIRTUAL_TAB, {
    target: 'All RDC Busy Banner',
    testType,
  });
};

const getLinkTarget = (mobileAppConfiguration) => mobileAppSelectionUtils.getUrlForMobileSelection(
  mobileAppConfiguration,
  undefined,
  TEST_CONFIGURATION_TABS.MOBILE_VIRTUAL,
);

const VmdBanner: React.FC = () => {
  const [showDevicesBusyMessage, setShowDevicesBusyMessage] = useState<boolean>(true);
  const {
    isBannerVisible,
    shouldTrackEventForActiveFreeUser,
    shouldTrackEventForDeviceSearch,
    shouldTrackEventForDeviceFilter,
    hasVmdConcurrencyAvailable,
    isVmdFriendlyConfiguration,
    testType,
    userType,
    outOfUsageUnits,
    usageUnit,
    creditRefreshDate,
  } = useSelector(selectors.vmdBannerSelector);
  const mobileAppConfiguration = useSelector<GlobalState, appStorageRecords.AppStorageFile | null>(
    testConfigSelectors.mobileAppConfigurationSelector,
  );

  useEffect(() => {
    if (shouldTrackEventForDeviceSearch && showDevicesBusyMessage) {
      trackEvent(EVENTS.MESSAGE_SHOWN, {
        subject_name: 'Try testing on VMD Banner',
        state: 'Device Search',
        user_type: userType,
        test_type: CONFIGURATION_TEST_TYPES[testType],
        product_sub_area: LIVE_TESTING_PRODUCT_SUB_AREAS.CONFIG_PAGE,
        ...LIVE_TESTING_TRACKING_PROPS,
      });
    }

    if (shouldTrackEventForActiveFreeUser && showDevicesBusyMessage) {
      trackEvent(EVENTS.MESSAGE_SHOWN, {
        subject_name: 'Try testing on VMD Banner',
        state: 'No Available Device',
        user_type: userType,
        test_type: CONFIGURATION_TEST_TYPES[testType],
        product_sub_area: LIVE_TESTING_PRODUCT_SUB_AREAS.CONFIG_PAGE,
        ...LIVE_TESTING_TRACKING_PROPS,
      });
    }

    if (shouldTrackEventForDeviceFilter && showDevicesBusyMessage) {
      trackEvent(EVENTS.MESSAGE_SHOWN, {
        subject_name: 'Try testing on VMD Banner',
        state: 'Device Filtered',
        user_type: userType,
        test_type: CONFIGURATION_TEST_TYPES[testType],
        product_sub_area: LIVE_TESTING_PRODUCT_SUB_AREAS.CONFIG_PAGE,
        ...LIVE_TESTING_TRACKING_PROPS,
      });
    }
  }, [
    shouldTrackEventForActiveFreeUser,
    shouldTrackEventForDeviceSearch,
    shouldTrackEventForDeviceFilter,
    showDevicesBusyMessage,
  ]);

  if (
    usageUnit === Units.Credits && outOfUsageUnits && userType !== ACCOUNT_TYPES[USER_TYPE_FREE]
  ) {
    const daysUntilRefresh = getDaysUntilRefresh(creditRefreshDate);
    return (
      <InlineAlert
        buttonProps={{
          children: 'Find a Plan',
          to: '/billing',
          variant: 'text',
        }}
        className="mt-4"
        color="info"
        data-qa="mobile-selection-rdc-credits-empty-message"
        title="No credits available"
      >
        {`Your credits will be refreshed in ${daysUntilRefresh} day${daysUntilRefresh > 1 ? 's' : ''}.`}
        {' To get more credits now, upgrade your plan.'}
      </InlineAlert>
    );
  }

  if (
    usageUnit === Units.Seconds && outOfUsageUnits && userType === ACCOUNT_TYPES[USER_TYPE_FREEMIUM]
  ) {
    return (
      <InlineAlert
        buttonProps={{
          children: 'Find a Plan',
          to: '/billing',
          variant: 'text',
        }}
        className="mb-4"
        color="info"
        data-qa="mobile-selection-rdc-seconds-empty-message"
        title="No minutes available."
      >
        {'You have exhausted all minutes. To keep testing, purchase a plan.'}
      </InlineAlert>
    );
  }

  if (isBannerVisible && showDevicesBusyMessage) {
    return (
      <InlineAlert
        className="mt-4"
        color="info"
        data-qa="mobile-selection-rdc-devices-busy-message"
        onDismiss={() => setShowDevicesBusyMessage(false)}
        title="No real devices available"
      >
        <div>
          <span>All real devices are currently in use. </span>
          {
            (isVmdFriendlyConfiguration && hasVmdConcurrencyAvailable) ? (
              <span>
                {'Try testing on a '}
                <Link
                  data-qa="mobile-selection-rdc-devices-busy-message-vmd-link"
                  onPress={() => trackClick(testType)}
                  to={() => getLinkTarget(mobileAppConfiguration)}
                >
                  virtual device
                </Link>
                {'!'}
              </span>
            ) :
              <span>Try again later!</span>
          }
        </div>
      </InlineAlert>
    );
  }

  return null;
};

export default VmdBanner;
